import { useEffect, useMemo, useRef, useState } from 'react'
import type { Cell, GridKey, LocaleInfo } from '../api/types'
import type { LocaleLabel } from '../lib/locales'
import { cellStatus } from '../lib/status'
import { FallbackMarker, StatusBadge } from '../components/StatusBadge'
import { cellKey, useUi, type CellKey } from '../store/ui'
import type { GridActions } from './Grid'

// --------------------------------------------------------------------------
// Cell
// --------------------------------------------------------------------------

export function GridCell({
  keyRow,
  locale,
  cell,
  allCells,
  rowIndex,
  colIndex,
  isCursor,
  actions,
}: {
  keyRow: GridKey
  locale: LocaleInfo
  cell: Cell | undefined
  /** Every CLDR form's cell for this (key, locale), not just the collapsed
   *  summary one -- used only to detect a plural key with some but not all
   *  required forms filled in, so the collapsed row can flag it. */
  allCells: Cell[] | undefined
  rowIndex: number
  colIndex: number
  isCursor: boolean
  actions: GridActions
}) {
  const setCursor = useUi((state) => state.setCursor)
  const editing = useUi((state) => state.editing)
  const setEditing = useUi((state) => state.setEditing)
  const openHistory = useUi((state) => state.openHistory)
  const lint = useUi((state) => state.lint)

  const pluralForm = cell?.plural_form ?? ''
  const ck = cellKey(keyRow.id, locale.code, pluralForm)
  const isEditing = editing === ck
  const readOnly = actions.readOnlyLocale(locale.code)
  const status = cell ? cellStatus(cell) : 'untranslated'
  const findings = lint[ck] ?? []
  // summaryCell shows 'other' (or whichever form happens to be first) on the
  // collapsed row, so a key with 'other' filled but 'one' missing used to
  // LOOK fully translated. Flag it the moment any of this locale's required
  // forms is still untranslated, without expanding the row.
  const incomplete =
    keyRow.is_plural && (allCells ?? []).some((c) => c.origin === 'untranslated')

  if (isEditing) {
    return (
      <CellEditor
        ck={ck}
        keyRow={keyRow}
        locale={locale.code}
        pluralForm={pluralForm}
        initial={cell?.value ?? ''}
        actions={actions}
        rowIndex={rowIndex}
        colIndex={colIndex}
      />
    )
  }

  const cellActions = [
    !locale.is_source && (
      <button
        key="retranslate"
        className="btn-icon"
        aria-label={`Re-translate ${keyRow.name} (${locale.code}) with AI`}
        title="Re-translate with AI (r)"
        onClick={(event) => {
          event.stopPropagation()
          actions.retranslate({ keyId: keyRow.id, locale: locale.code })
        }}
      >
        <span aria-hidden="true">◆</span>
      </button>
    ),
    cell?.translation_id && (
      <button
        key="history"
        className="btn-icon"
        aria-label={`History for ${keyRow.name} (${locale.code})`}
        title="History and revert (h)"
        onClick={(event) => {
          event.stopPropagation()
          openHistory({
            translationId: cell.translation_id!,
            keyName: keyRow.name,
            locale: locale.code,
          })
        }}
      >
        <span aria-hidden="true">↺</span>
      </button>
    ),
  ].filter(Boolean)

  return (
    <div
      role="gridcell"
      data-cell={`${rowIndex}:${colIndex}`}
      aria-colindex={colIndex + 3}
      tabIndex={isCursor ? 0 : -1}
      aria-readonly={readOnly}
      aria-label={`${keyRow.name}, ${locale.code}: ${cell?.value ?? 'untranslated'}`}
      onFocus={() => setCursor({ row: rowIndex, col: colIndex })}
      onClick={() => {
        setCursor({ row: rowIndex, col: colIndex })
        if (!readOnly) setEditing(ck)
      }}
      className={`group min-w-0 cursor-text border-l px-2 py-1 text-xs ${
        isCursor ? 'border-l-blue-500 ring-1 ring-inset ring-blue-500' : 'border-ink-100'
      } ${readOnly ? 'cursor-default bg-ink-50/60' : ''}`}
    >
      <div className="flex items-start gap-1.5">
        <span className="shrink-0 pt-px">
          <StatusBadge kind={status} compact />
        </span>
        {cell?.origin === 'fallback' && (
          <span className="shrink-0 pt-px">
            <FallbackMarker compact />
          </span>
        )}
        {incomplete && (
          <span
            className="chip shrink-0 border-amber-300 bg-amber-50 text-amber-900"
            title={`${keyRow.name} (${locale.code}): at least one required CLDR plural form is still untranslated — expand the row to see which.`}
          >
            <span aria-hidden="true">⚠</span> incomplete
          </span>
        )}
        <span
          className={`min-w-0 flex-1 whitespace-pre-wrap break-words ${
            cell?.origin === 'fallback' ? 'cell-fallback' : cell?.value ? 'text-ink-900' : 'text-ink-600'
          }`}
        >
          {/* Floated, not absolutely positioned: the buttons reserve their own
              corner so the text wraps around them instead of being painted
              over. `invisible` keeps the space (no reflow on hover) and keeps
              them out of the tab order until the cell is hovered or focused. */}
          {cellActions.length > 0 && (
            <span className="invisible float-right ml-1.5 flex gap-0.5 group-hover:visible group-focus-within:visible">
              {cellActions}
            </span>
          )}
          {cell?.value ?? '—'}
        </span>
      </div>
      {findings.length > 0 && (
        <div className="mt-0.5 flex flex-wrap items-center gap-1">
          {findings.map((finding) => (
            <span
              key={finding.code}
              className="chip border-rose-300 bg-rose-50 text-rose-900"
              title={finding.message}
            >
              <span aria-hidden="true">⚠</span> {finding.code}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function CellEditor({
  ck,
  keyRow,
  locale,
  pluralForm,
  initial,
  actions,
  rowIndex,
  colIndex,
}: {
  ck: CellKey
  keyRow: GridKey
  locale: string
  pluralForm: string
  initial: string
  actions: GridActions
  rowIndex: number
  colIndex: number
}) {
  const [value, setValue] = useState(initial)
  const setEditing = useUi((state) => state.setEditing)
  const setCursor = useUi((state) => state.setCursor)
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return
    element.focus()
    element.setSelectionRange(element.value.length, element.value.length)
  }, [])

  const commit = () => {
    if (value !== initial) actions.save({ keyId: keyRow.id, locale, pluralForm, value })
  }

  return (
    <div
      className="relative min-w-0 border-l border-blue-500 bg-white p-1 ring-2 ring-inset ring-blue-500"
      data-cell={`${rowIndex}:${colIndex}`}
    >
      <textarea
        ref={ref}
        className="field min-h-[3.5rem] resize-y px-1.5 py-1 text-xs"
        value={value}
        aria-label={`Edit ${keyRow.name} (${locale}${pluralForm ? `, ${pluralForm}` : ''})`}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => {
          commit()
          setEditing(null)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            setValue(initial)
            setEditing(null)
            return
          }
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            event.stopPropagation()
            commit()
            setEditing(null)
            return
          }
          if (event.key === 'Tab') {
            event.preventDefault()
            event.stopPropagation()
            commit()
            setEditing(null)
            setCursor({ row: rowIndex, col: colIndex + (event.shiftKey ? -1 : 1) })
          }
        }}
      />
      <p className="mt-0.5 text-2xs text-ink-600">
        <kbd className="font-mono">⌘↵</kbd> save · <kbd className="font-mono">esc</kbd> cancel ·{' '}
        <kbd className="font-mono">⇥</kbd> next cell
      </p>
      <span className="sr-only" id={`hint-${ck}`}>
        Command or Control Enter saves, Escape cancels, Tab saves and moves to the next cell.
      </span>
    </div>
  )
}

// --------------------------------------------------------------------------
// Row expansion: plural forms + the key's context note
// --------------------------------------------------------------------------

export function RowDetail({
  keyRow,
  locales,
  labels,
  actions,
}: {
  keyRow: GridKey
  locales: LocaleInfo[]
  labels: Record<string, LocaleLabel>
  actions: GridActions
}) {
  const [context, setContext] = useState(keyRow.context ?? '')
  const forms = useMemo(() => {
    const set = new Set<string>()
    for (const locale of locales) {
      for (const cell of keyRow.cells[locale.code] ?? []) set.add(cell.plural_form)
    }
    return Array.from(set).sort()
  }, [keyRow, locales])

  return (
    <div className="border-t border-dashed border-ink-200 bg-ink-50/70 px-3 py-2">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button className="btn" onClick={() => actions.openRename(keyRow)}>
          Rename key…
        </button>
        <label className="flex items-center gap-1.5 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={keyRow.is_plural}
            title={
              keyRow.is_plural
                ? "Convert to singular. Refused if any CLDR form other than 'other' still holds a translation."
                : "Convert to plural. The existing value lands on the CLDR 'other' form."
            }
            onChange={(event) =>
              actions.setIsPlural({ keyId: keyRow.id, isPlural: event.target.checked })
            }
          />
          Plural key
        </label>
        <DeprecateControl keyRow={keyRow} actions={actions} />
      </div>
      {keyRow.is_plural && (
        <div className="mb-3">
          <h4 className="label mb-1">Plural forms (CLDR)</h4>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] text-xs">
              <thead>
                <tr className="text-left text-2xs uppercase tracking-wider text-ink-600">
                  <th className="w-20 py-1 font-semibold">Form</th>
                  {locales.map((locale) => (
                    <th key={locale.code} className="py-1 font-semibold" title={labels[locale.code]?.full}>
                      {labels[locale.code]?.short ?? locale.code}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {forms.map((form) => (
                  <tr key={form} className="align-top">
                    <td className="py-1 pr-2 font-mono text-2xs text-ink-600">{form || '—'}</td>
                    {locales.map((locale) => {
                      const cell = (keyRow.cells[locale.code] ?? []).find(
                        (item) => item.plural_form === form,
                      )
                      // A row axis is shared across every locale in view (so
                      // columns line up), but not every locale needs every
                      // form -- German has no 'few', Japanese has only
                      // 'other'. A form this locale's CLDR rule does not use
                      // gets a plain dash, never an editable input: showing
                      // an input there would invite typing a translation
                      // that can never be exported.
                      const applicable = locale.plural_forms.includes(form)
                      return (
                        <td key={locale.code} className="py-1 pr-2">
                          {applicable ? (
                            <PluralInput
                              keyId={keyRow.id}
                              locale={locale.code}
                              pluralForm={form}
                              value={cell?.value ?? ''}
                              origin={cell?.origin ?? 'untranslated'}
                              readOnly={actions.readOnlyLocale(locale.code)}
                              onSave={(next) =>
                                actions.save({
                                  keyId: keyRow.id,
                                  locale: locale.code,
                                  pluralForm: form,
                                  value: next,
                                })
                              }
                            />
                          ) : (
                            <span
                              className="block px-1.5 py-1 text-center text-ink-400"
                              title={`${labels[locale.code]?.full ?? locale.code} does not use the '${form}' CLDR form`}
                            >
                              n/a
                            </span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="max-w-3xl">
        <label className="label" htmlFor={`context-${keyRow.id}`}>
          Context note for translators and the AI
        </label>
        <textarea
          id={`context-${keyRow.id}`}
          className="field mt-1 min-h-[3rem] text-xs"
          value={context}
          placeholder="Where this string appears, length limits, tone…"
          onChange={(event) => setContext(event.target.value)}
        />
        <div className="mt-1 flex items-center gap-2">
          <button
            className="btn btn-primary"
            disabled={context === (keyRow.context ?? '')}
            onClick={() => actions.saveContext({ keyId: keyRow.id, context })}
          >
            Save context
          </button>
          <button
            className="btn"
            disabled={context === (keyRow.context ?? '')}
            onClick={() => setContext(keyRow.context ?? '')}
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  )
}

/** States the real effect, not a vague warning: deprecating pulls the key
 *  from every export immediately (apps stop receiving it on their next
 *  pull) while keeping its translations and history, reversibly. */
const DEPRECATE_WARNING =
  'Deprecate this key? It is removed from every export immediately — apps stop receiving it ' +
  'on their next pull. Its translations and history are kept, and you can restore it anytime ' +
  'from this same row.'

function DeprecateControl({
  keyRow,
  actions,
}: {
  keyRow: GridKey
  actions: GridActions
}) {
  if (keyRow.deprecated) {
    return (
      <span className="flex items-center gap-2">
        <span
          className="chip border-rose-300 bg-rose-50 text-rose-900"
          title="Removed from every export; translations and history are kept."
        >
          deprecated
        </span>
        <button
          className="btn"
          onClick={() => actions.setDeprecated({ keyId: keyRow.id, deprecated: false })}
        >
          Restore key
        </button>
        <span className="text-2xs text-ink-600">
          Restoring brings it back into every export on the next pull.
        </span>
      </span>
    )
  }
  return (
    <button
      className="btn"
      title="Removes the key from every export immediately; reversible."
      onClick={() => {
        if (window.confirm(DEPRECATE_WARNING)) {
          actions.setDeprecated({ keyId: keyRow.id, deprecated: true })
        }
      }}
    >
      Deprecate key…
    </button>
  )
}

function PluralInput({
  keyId,
  locale,
  pluralForm,
  value,
  origin,
  readOnly,
  onSave,
}: {
  keyId: string
  locale: string
  pluralForm: string
  value: string
  origin: Cell['origin']
  readOnly: boolean
  onSave: (value: string) => void
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  return (
    <input
      className={`field px-1.5 py-1 text-xs ${origin === 'fallback' ? 'cell-fallback' : ''}`}
      value={draft}
      readOnly={readOnly}
      aria-label={`${keyId} ${locale} ${pluralForm || 'singular'}`}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => draft !== value && onSave(draft)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          if (draft !== value) onSave(draft)
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          setDraft(value)
        }
      }}
    />
  )
}
